import { defineComponent as _defineComponent } from 'vue'
import { computed, defineComponent, ref, shallowRef } from "vue"

type Props = {
  value?: string | string[]
  mode?: "tags" | "multiple" | "default"
  defaultOptions?: any[]
  isLoading?: boolean
  isDisabled?: boolean
  customRequest?: Function
  customRequestParams?: Record<string, string | number | null>
  valueKey?: string
  labelKey?: string
  searchKey?: string
  allowClear?: boolean
  placeholder?: string
  onFocusFetch?: boolean
  showSearch?: boolean
  optionLabelProp?: string
}
type Emits = {
  (e: "change", value: string | string[]): void
  (e: "focus"): void
  (e: "blur"): void
  (e: "scroll"): void
  (e: "search", searchValue?: string): void
}


export default /*#__PURE__*/_defineComponent({
  __name: 'index',
  props: {
    value: null,
    mode: { default: "default" },
    defaultOptions: { default: () => [] },
    isLoading: { type: Boolean, default: false },
    isDisabled: { type: Boolean, default: false },
    customRequest: null,
    customRequestParams: { default: () => ({}) },
    valueKey: { default: "oid" },
    labelKey: { default: "name" },
    searchKey: { default: "" },
    allowClear: { type: Boolean, default: true },
    placeholder: { default: "" },
    onFocusFetch: { type: Boolean, default: false },
    showSearch: { type: Boolean, default: true },
    optionLabelProp: { default: "children" }
  },
  emits: ["change", "focus", "blur", "scroll", "search"],
  setup(__props: any, { expose, emit }: { emit: ({
  (e: "change", value: string | string[]): void
  (e: "focus"): void
  (e: "blur"): void
  (e: "scroll"): void
  (e: "search", searchValue?: string): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as { value?: string | string[], mode: "tags" | "multiple" | "default", defaultOptions: any[], isLoading: boolean, isDisabled: boolean, customRequest?: Function, customRequestParams: Record<string, string | number | null>, valueKey: string, labelKey: string, searchKey: string, allowClear: boolean, placeholder: string, onFocusFetch: boolean, showSearch: boolean, optionLabelProp: string };

const SelectNodes = defineComponent({
  props: {
    vnodes: {
      type: Object,
      required: true
    }
  },
  render() {
    return this.vnodes
  }
})




const abortController = ref<AbortController>()
const searchTimeout = ref()

const onChange = (value) => {
  emit("change", value)
}
const onFocus = () => {
  emit("focus")

  if (!props.onFocusFetch || dataSource.value.length) return

  handleSearchOptions()
}
const onBlur = () => {
  lastSearchedValue.value = ""
  emit("blur")
}

const isFetching = shallowRef<boolean>(false)
const lastSearchedValue = ref<string>("")
const dataSource = ref([])
const dataSourceCount = ref<number>()

const selectOptions = computed(() => {
  if (!props.defaultOptions.length) return dataSource.value

  const seenKeys = new Set(props.defaultOptions.map((opt) => opt[props.valueKey]))
  const mergedOptions = [
    ...props.defaultOptions,
    ...dataSource.value.filter((item) => {
      if (seenKeys.has(item[props.valueKey])) return false

      seenKeys.add(item[props.valueKey])
      return true
    })
  ]
  return mergedOptions
})

const onResetOptions = () => {
  dataSource.value.length = 0
  dataSourceCount.value = undefined
}
const onSearch = (value: string = "") => {
  if (searchTimeout.value) {
    clearTimeout(searchTimeout.value)
    searchTimeout.value = undefined
  }

  lastSearchedValue.value = value.trim()

  searchTimeout.value = setTimeout(() => {
    if (!props.customRequest) return emit("search", lastSearchedValue.value)

    onResetOptions()

    handleSearchOptions()

    searchTimeout.value = undefined
  }, 500)
}
const handleSearchOptions = async () => {
  if (!props.customRequest) return

  try {
    if (abortController.value) {
      abortController.value.abort()
      abortController.value = undefined
    }

    abortController.value = new AbortController()

    isFetching.value = true

    const searchKey = props.searchKey || props.labelKey

    const { data } = await props.customRequest({
      queryParams: {
        ...props.customRequestParams,
        [searchKey]: lastSearchedValue.value,
        pageSize: 10,
        page: Math.floor(dataSource.value.length / 10) + 1
      },
      signal: abortController.value.signal
    })

    dataSource.value.push(...data.results)
    dataSourceCount.value = data.count
    isFetching.value = false
  } catch (error) {
    if (error.message === "canceled") return

    isFetching.value = false
  }
}
const onScroll = async (event) => {
  if (event.target?.scrollHeight - event.target?.clientHeight > event.target?.scrollTop + 10) return

  if (!props.customRequest) return emit("scroll")

  if (isFetching.value) return

  if (selectOptions.value.length >= dataSourceCount.value) return

  handleSearchOptions()
}

expose({
  selectOptions,
  onResetOptions
})

return { __sfc: true,SelectNodes, props, emit, abortController, searchTimeout, onChange, onFocus, onBlur, isFetching, lastSearchedValue, dataSource, dataSourceCount, selectOptions, onResetOptions, onSearch, handleSearchOptions, onScroll }
}

})